<script setup>
import { ref, computed, onMounted } from 'vue'
import { listMenu } from '@/api/system/menu'

const loading = ref(false)
const query = ref({ menuName: '', status: '' })
const statusOptions = [
  { label: 'Enabled', value: '0' },
  { label: 'Disabled', value: '1' },
]

const menus = ref([])

const fetchMenus = async () => {
  loading.value = true
  try {
    const res = await listMenu({
      menuName: query.value.menuName || undefined,
      status: query.value.status || undefined,
    })
    // assuming backend returns tree data in res.data or res; handle both
    const data = res?.data || res || []
    menus.value = data
  } catch (err) {
    console.error('Failed to load menus', err)
    menus.value = []
  } finally {
    loading.value = false
  }
}

const filteredMenus = computed(() => {
  const name = query.value.menuName.trim().toLowerCase()
  const status = query.value.status
  const filterTree = (items = []) =>
    items
      .map((item) => {
        const matchName = !name || item.menuName?.toLowerCase().includes(name)
        const matchStatus = !status || item.status === status
        const children = filterTree(item.children)
        const matched = (matchName && matchStatus) || children.length
        return matched ? { ...item, children } : null
      })
      .filter(Boolean)
  return filterTree(menus.value)
})

const handleQuery = () => fetchMenus()
const resetQuery = () => {
  query.value = { menuName: '', status: '' }
  fetchMenus()
}
const toggleStatus = (item) => {
  item.status = item.status === '0' ? '1' : '0'
}

onMounted(() => {
  fetchMenus()
})
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Menu Management</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card mb-4">
      <v-card-title>Filters</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field v-model="query.menuName" label="Menu Name" dense clearable hide-details />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="query.status" :items="statusOptions" item-title="label" item-value="value" label="Status" dense clearable hide-details />
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center flex-wrap gap-3">
            <v-btn size="small" color="primary" @click="handleQuery">Search</v-btn>
            <v-btn size="small" variant="tonal" color="primary" @click="resetQuery">Reset</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="section-card">
      <v-card-title>Menu List</v-card-title>
      <v-data-table
        :items="filteredMenus"
        :headers="[
          { title: 'Menu Name', key: 'menuName' },
          { title: 'Icon', key: 'icon', width: 80 },
          { title: 'Order', key: 'orderNum', width: 90 },
          { title: 'Permission', key: 'perms' },
          { title: 'Component Path', key: 'component' },
          { title: 'Status', key: 'status', width: 150 },
          { title: 'Create Time', key: 'createTime', width: 180 },
        ]"
        class="elevation-1 monitoring-table"
        :loading="loading"
        disable-sort
        disable-pagination
        hide-default-footer
        density="comfortable"
      >
        <template #item.menuName="{ item }">
          <div :style="{ paddingLeft: `${(item.level || 0) * 16}px` }" class="d-flex align-center gap-2">
            <v-icon size="18" class="text-muted">mdi-subdirectory-arrow-right</v-icon>
            <span>{{ item.menuName }}</span>
          </div>
        </template>
        <template #item.icon="{ item }">
          <v-icon size="18">{{ item.icon || 'mdi-menu' }}</v-icon>
        </template>
        <template #item.status="{ item }">
          <div class="d-flex align-center">
            <v-chip :color="item.status === '0' ? 'success' : 'grey'" size="small" variant="flat" class="mr-2">
              {{ item.status === '0' ? 'Enabled' : 'Disabled' }}
            </v-chip>
            <v-switch density="compact" :model-value="item.status === '0'" @change="toggleStatus(item)" hide-details inset />
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}
</style>
